import { useState, useEffect } from 'react';
import { AlertTriangle, Clock, CheckCircle, Search, ChevronRight, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

import { API_URL } from '@/config/api';

export default function AdminFoodSafety() {
  const { token } = useAuth();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedCase, setSelectedCase] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [updateData, setUpdateData] = useState({ investigation_note: '', resolution: '', corrective_action: '' });

  useEffect(() => {
    fetchCases();
  }, [token, statusFilter]);

  const fetchCases = async () => {
    try {
      const url = statusFilter === 'all'
        ? `${API_URL}/api/admin/food-safety`
        : `${API_URL}/api/admin/food-safety?status=${statusFilter}`;

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setCases(data);
      }
    } catch (error) {
      console.error('Error fetching cases:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateCase = async (caseId, updates) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/food-safety/${caseId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify(updates)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update case');
      }

      toast.success('Case updated');
      setSheetOpen(false);
      setSelectedCase(null);
      setUpdateData({ investigation_note: '', resolution: '', corrective_action: '' });
      fetchCases();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const getPriorityClass = (priority) => {
    const classes = {
      high: 'border-l-4 border-l-red-500 bg-red-50/50',
      medium: 'border-l-4 border-l-orange-500 bg-orange-50/50',
      low: 'border-l-4 border-l-blue-500 bg-blue-50/50'
    };
    return classes[priority] || 'bg-gray-50';
  };

  const getStatusIcon = (status) => {
    const icons = {
      open: <AlertTriangle className="h-4 w-4 text-red-600" />,
      under_investigation: <Clock className="h-4 w-4 text-orange-600" />,
      closed: <CheckCircle className="h-4 w-4 text-green-600" />
    };
    return icons[status];
  };

  const openCaseDetails = (caseItem) => {
    setSelectedCase(caseItem);
    setUpdateData({ investigation_note: '', resolution: '', corrective_action: '' });
    setSheetOpen(true);
  };

  return (
    <div data-testid="admin-food-safety" className="pb-20 md:pb-0">
      <div className="mb-6 md:mb-8">
        <h1 className="font-heading text-2xl md:text-3xl font-semibold text-secondary-900">Food Safety Cases</h1>
        <p className="text-sm md:text-base text-muted-foreground">Manage and investigate food safety incidents</p>
      </div>

      {/* Filter - Mobile Optimized */}
      <Card className="mb-4 md:mb-6">
        <CardContent className="p-3 md:p-4">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full md:w-48" data-testid="status-filter">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Cases</SelectItem>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="under_investigation">Under Investigation</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      ) : cases.length > 0 ? (
        <div className="space-y-3 md:space-y-4">
          {cases.map(caseItem => (
            <Card 
              key={caseItem.case_id} 
              className={`${getPriorityClass(caseItem.priority)} cursor-pointer hover:shadow-md transition-shadow`}
              onClick={() => openCaseDetails(caseItem)}
              data-testid={`case-${caseItem.case_id}`}
            >
              <CardContent className="p-4 md:p-6">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    {/* Header Row */}
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      {getStatusIcon(caseItem.status)}
                      <Badge className={`${
                        caseItem.priority === 'high' ? 'bg-red-600' :
                        caseItem.priority === 'medium' ? 'bg-orange-600' : 'bg-blue-600'
                      } text-white text-xs`}>
                        {caseItem.priority.toUpperCase()}
                      </Badge>
                      <span className="font-mono text-xs text-muted-foreground hidden sm:inline">{caseItem.case_id}</span>
                    </div>
                    
                    {/* Meal Name */}
                    <h3 className="font-semibold text-base md:text-lg text-secondary-900 mb-1 truncate">{caseItem.meal_name}</h3>
                    
                    {/* Description - truncate on mobile */}
                    <p className="text-sm text-secondary-700 mb-2 line-clamp-2">{caseItem.description}</p>
                    
                    {/* Key Info - Compact on mobile */}
                    <div className="text-xs md:text-sm text-muted-foreground space-y-0.5">
                      <p className="truncate"><strong>Customer:</strong> {caseItem.customer_name}</p>
                      <p><strong>Type:</strong> {caseItem.concern_type.replace('_', ' ')}</p>
                      <p><strong>Date:</strong> {caseItem.consumption_date}</p>
                    </div>
                  </div>
                  
                  {/* Arrow indicator for mobile */}
                  <ChevronRight className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-1" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="p-8 md:p-12 text-center">
            <CheckCircle className="h-10 w-10 md:h-12 md:w-12 text-green-600 mx-auto mb-4" />
            <p className="text-muted-foreground">No food safety cases found</p>
          </CardContent>
        </Card>
      )}

      {/* Mobile-First Sheet for Case Details */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent side="bottom" className="h-[90vh] md:h-auto md:max-h-[90vh] overflow-y-auto rounded-t-2xl md:rounded-none">
          {selectedCase && (
            <>
              <SheetHeader className="sticky top-0 bg-background pb-4 border-b">
                <div className="flex items-center justify-between">
                  <SheetTitle className="text-lg md:text-xl">Case Details</SheetTitle>
                  <Badge className={`${
                    selectedCase.status === 'open' ? 'bg-red-600' :
                    selectedCase.status === 'under_investigation' ? 'bg-orange-600' : 'bg-green-600'
                  } text-white`}>
                    {selectedCase.status.replace('_', ' ').toUpperCase()}
                  </Badge>
                </div>
                <p className="text-xs font-mono text-muted-foreground">{selectedCase.case_id}</p>
              </SheetHeader>

              <div className="space-y-6 pt-4 pb-8">
                {/* Case Info Grid */}
                <div className="grid grid-cols-2 gap-3 md:gap-4">
                  <div className="bg-muted/50 p-3 rounded-lg">
                    <Label className="text-xs text-muted-foreground">Priority</Label>
                    <p className="font-semibold text-sm">{selectedCase.priority.toUpperCase()}</p>
                  </div>
                  <div className="bg-muted/50 p-3 rounded-lg">
                    <Label className="text-xs text-muted-foreground">Concern Type</Label>
                    <p className="font-semibold text-sm">{selectedCase.concern_type.replace('_', ' ')}</p>
                  </div>
                </div>

                {/* Customer & Meal Info */}
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">Meal</Label>
                    <p className="font-semibold">{selectedCase.meal_name}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Customer</Label>
                    <p className="font-medium">{selectedCase.customer_name}</p>
                    <p className="text-sm text-muted-foreground">{selectedCase.customer_email}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Consumption</Label>
                    <p className="text-sm">{selectedCase.consumption_date} at {selectedCase.consumption_time}</p>
                  </div>
                </div>

                {/* Description */}
                <div>
                  <Label className="text-xs text-muted-foreground">Description</Label>
                  <p className="text-sm mt-1">{selectedCase.description}</p>
                </div>

                {/* Symptoms */}
                {selectedCase.has_symptoms && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <Label className="text-xs text-red-700">Reported Symptoms</Label>
                    <p className="text-sm text-red-900 mt-1">{selectedCase.symptoms}</p>
                  </div>
                )}

                {/* Investigation Notes */}
                {selectedCase.investigation_notes?.length > 0 && (
                  <div>
                    <Label className="text-xs text-muted-foreground mb-2 block">Investigation Notes</Label>
                    <div className="space-y-2">
                      {selectedCase.investigation_notes.map((note, idx) => (
                        <div key={idx} className="bg-muted p-3 rounded-lg text-sm">
                          <p>{note.note}</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            By {note.added_by_name} - {new Date(note.added_at).toLocaleString()}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Action Forms */}
                {selectedCase.status !== 'closed' && (
                  <div className="space-y-4 pt-4 border-t">
                    <h4 className="font-semibold">Update Case</h4>
                    
                    {/* Add Note */}
                    <div className="space-y-2">
                      <Label className="text-sm">Add Investigation Note</Label>
                      <Textarea
                        value={updateData.investigation_note}
                        onChange={(e) => setUpdateData(prev => ({ ...prev, investigation_note: e.target.value }))}
                        placeholder="Add notes about the investigation..."
                        className="min-h-[80px]"
                        data-testid="investigation-note"
                      />
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="flex flex-col sm:flex-row gap-2">
                      {selectedCase.status === 'open' && (
                        <Button
                          onClick={() => updateCase(selectedCase.case_id, {
                            status: 'under_investigation',
                            investigation_note: updateData.investigation_note || null
                          })}
                          className="bg-orange-600 hover:bg-orange-700 flex-1"
                          data-testid="start-investigation"
                        >
                          Start Investigation
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        onClick={() => updateCase(selectedCase.case_id, {
                          investigation_note: updateData.investigation_note
                        })}
                        disabled={!updateData.investigation_note}
                        className="flex-1"
                        data-testid="add-note-btn"
                      >
                        Add Note Only
                      </Button>
                    </div>

                    {/* Close Case Form */}
                    {selectedCase.status === 'under_investigation' && (
                      <div className="space-y-3 pt-4 border-t">
                        <h4 className="font-semibold">Close Case</h4>
                        <div className="space-y-2">
                          <Label className="text-sm">Resolution *</Label>
                          <Textarea
                            value={updateData.resolution}
                            onChange={(e) => setUpdateData(prev => ({ ...prev, resolution: e.target.value }))}
                            placeholder="Describe the resolution..."
                            className="min-h-[80px]"
                            data-testid="resolution-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm">Corrective Action</Label>
                          <Textarea
                            value={updateData.corrective_action}
                            onChange={(e) => setUpdateData(prev => ({ ...prev, corrective_action: e.target.value }))}
                            placeholder="Describe preventative steps taken..."
                            className="min-h-[80px]"
                            data-testid="corrective-action-input"
                          />
                        </div>
                        <Button
                          onClick={() => updateCase(selectedCase.case_id, {
                            status: 'closed',
                            resolution: updateData.resolution,
                            corrective_action: updateData.corrective_action || null
                          })}
                          disabled={!updateData.resolution}
                          className="w-full bg-green-600 hover:bg-green-700"
                          data-testid="close-case-btn"
                        >
                          Close Case
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {/* Closed Case Info */}
                {selectedCase.status === 'closed' && (
                  <div className="space-y-3 pt-4 border-t">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                      <Label className="text-xs text-green-700">Resolution</Label>
                      <p className="text-sm text-green-900 mt-1">{selectedCase.resolution}</p>
                    </div>
                    {selectedCase.corrective_action && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <Label className="text-xs text-blue-700">Corrective Action</Label>
                        <p className="text-sm text-blue-900 mt-1">{selectedCase.corrective_action}</p>
                      </div>
                    )}
                    <p className="text-xs text-muted-foreground text-center">
                      Closed on {new Date(selectedCase.closed_at).toLocaleString()}
                    </p>
                  </div>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
